import config from 'config'
import { Tile } from 'client/components/common/Tile'
import { Typography, Icon, Link, Box } from '@vp/swan'
import { getLocaleSelector, getMpvid } from 'client/store/config'
import { useSelector } from 'react-redux'
import { useTranslations } from 'client/hooks/useTranslations'
import { buildFullyQualifiedVistaprintUrl } from 'client/utils/vistaprintUrlBuilder'
import { useAnalytics } from 'client/hooks/gallery/useAnalytics'
import { ANALYTICS_PAGE_ZONE, ANALYTICS_CTA_VALUE, URL_TARGET } from 'client/components/Gallery/PreviewArea/CareTile/constants'
import { ANALYTICS_EVENT_ACTIONS } from 'shared/constants'
import { useExperimentation } from '~/client/hooks/useExperimentation'
import { DIFY_TILE_TEST, DIFY_TILE_TEST_1, DIFY_TILE_TEST_2 } from 'shared/experiments/DifyTileTest/constants'
import { usePathname } from '~/client/hooks/usePathname'

const DESIGNERS_IMAGE = 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/Gallery/assets/care-tile-designers.png'

export const CareTile = () => {
  const localize = useTranslations()
  const locale = useSelector(getLocaleSelector)
  const mpvId = useSelector(getMpvid)

  const url = buildFullyQualifiedVistaprintUrl({
    path: URL_TARGET,
    locale,
    mpvId,
  })
  const analytics = useAnalytics()
  const pathname = usePathname()
  const isExperimentActive = useExperimentation()
  const isDifyTileTest1Active = isExperimentActive(DIFY_TILE_TEST.Enabled1)
  const isDifyTileTest2Active = isExperimentActive(DIFY_TILE_TEST.Enabled2)

  const getCareTileCTAText = (): string => {
    if (isDifyTileTest1Active) {
      return DIFY_TILE_TEST_1.CTA_TEXT
    }
    if (isDifyTileTest2Active) {
      return DIFY_TILE_TEST_2.CTA_TEXT
    }
    return localize('CareTileCTAText')
  }

  const getCareTileCaptionText = (): string => {
    if (isDifyTileTest1Active) {
      return DIFY_TILE_TEST_1.CAPTION_TEXT
    }
    if (isDifyTileTest2Active) {
      return DIFY_TILE_TEST_2.CAPTION_TEXT
    }
    return localize('CareTileCaptionTextVariant')
  }

  const careTileCTAText = getCareTileCTAText()
  const careTileCaptionText = getCareTileCaptionText()
  const careTileHeadingText = isDifyTileTest2Active ? DIFY_TILE_TEST_2.HEADING_TEXT : localize('CareTileHeadingVariant')

  const onClick = (): void => {
    const eventDetailList = [pathname, URL_TARGET, ANALYTICS_PAGE_ZONE.CARE_TILE, ANALYTICS_CTA_VALUE.EXPERT_SERVICES_DESIGN_SERVICES_BRIEF]

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.NAVIGATION_CLICKED,
      eventLabel: `Content:${ANALYTICS_PAGE_ZONE.CARE_TILE}`,
      eventDetail: eventDetailList.join(';'),
      navigationDetail: ANALYTICS_CTA_VALUE.EXPERT_SERVICES_DESIGN_SERVICES_BRIEF,
      route: config.client.segmentRoute,
    })
  }

  return (
    <Tile type="care-tile care-tile-dark" data-testid="care-tile">
      <Link
        aria-label={localize('CareTileHeading')}
        className="care-tile-action-area"
        href={url}
        referrerPolicy="no-referrer-when-downgrade"
        skin="unstyled"
        onClick={onClick}
      >
        <div className="care-tile-content">
          <Box px={{ lg: 4, xl: 7 }}>
            <Box
              py={{
                xs: 2,
                md: 3,
                lg: 5,
                xl: 7,
              }}
            >
              <img alt={localize('CareTileImageDescription')} className="care-tile-designer-img" src={DESIGNERS_IMAGE} />
            </Box>
            <Typography className="care-tile-header-text" fontWeight="bold">
              {careTileHeadingText}
            </Typography>
            <Typography fontSize="small">{careTileCaptionText}</Typography>
            <Box paddingTop={5}>
              <Typography className="care-tile-cta" fontWeight="bold">
                <span className="care-tile-cta-text">{careTileCTAText}</span>
                <span>
                  <Icon darkMode iconType="arrowRight" />
                </span>
              </Typography>
            </Box>
          </Box>
        </div>
      </Link>
    </Tile>
  )
}

CareTile.displayName = 'CareTile'
